// src/pages/reports/ScheduledReportsTable.jsx
import React, { useEffect, useRef, useState } from "react";
import {
    Row, Col, CardHeader, CardBody,
    Spinner, Alert, Input, Button, Badge
} from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { API } from "../../api/urls";
import useAnimatedPlaceholder, { SEARCH_FIELDS } from "../../hooks/useAnimatedPlaceholder";

import ScheduledReportCreateModal from "../modals/report/ScheduledReportCreateModal";
import ScheduledReportEditModal from "../modals/report/ScheduledReportEditModal";
import ScheduledReportDeleteModal from "../modals/report/ScheduledReportDeleteModal";
import ScheduledReportPreviewModal from "../modals/report/ScheduledReportPreviewModal";
import TruncatedText from "../../components/Common/TruncatedText"
import { ACCOUNT_TYPE_LABEL_MAP, ACCOUNT_TYPES, ACCOUNT_TYPE_BADGE_COLORS } from "../../constants/accountTypes";

// ---- Reusable TableCard (CompaniesTable ile aynı görünüm) ----
const TableCard = ({ className = "", children, ...rest }) => (
    <div
        className={`card rounded-4 border-0 shadow-lg ${className}`}
        style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
        {...rest}
    >
        {children}
    </div>
);

// ---- Kurumsal renkler ----
const NAVY = "#221C5D";
const ORANGE = "#E56A22";

// ---- Çeviri yardımcıları ----
const translateScheduleType = (t) => ({
    DAILY: "Günlük",
    WEEKLY: "Haftalık",
    MONTHLY: "Aylık",
    QUARTERLY: "3 Aylık"
}[t] || t || "-");

// Cari türü Türkçe etiketleri merkezi kaynaktan gelir (backend AccountType enum ile hizalı).
// Böylece backend'e yeni tür eklendiğinde tek yerden (accountTypes.js) güncellenir.
const ALL_ACCOUNT_TYPES = ACCOUNT_TYPES.map((t) => t.value);

const translateAccountType = (t) => ACCOUNT_TYPE_LABEL_MAP[t] || t || "-";

const translatePaymentStatus = (s) => ({
    SUCCESS: "Başarılı",
    PENDING: "Beklemede",
    FAILED: "Başarısız"
}[s] || s || "-");

const formatDate = (date) =>
    date
        ? new Date(date).toLocaleString("tr-TR", {
            year: "numeric", month: "2-digit", day: "2-digit",
            hour: "2-digit", minute: "2-digit",
        })
        : "-";

const ScheduledReportsTable = () => {
    // data
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [err, setErr] = useState(null);

    // pagination
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // filters
    const [scheduleType, setScheduleType] = useState("");
    const [emailLike, setEmailLike] = useState("");
    const [companyId, setCompanyId] = useState(""); // opsiyonel (SA için)
    const [accountId, setAccountId] = useState("");

    const isFirstSearch = useRef(true);
    const emailRef = useRef(null);
    const animatedPlaceholder = useAnimatedPlaceholder(SEARCH_FIELDS.scheduledReports, { prefix: "Ara: " });
    const navigate = useNavigate();

    document.title = "Zamanlanmış Raporlar | Tahsilat360";

    const styles = `
    .sr-modern .table thead tr { background-color: #f9f9f9; }
    .sr-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
    .sr-modern .table td { vertical-align: middle; }
    .sr-modern .table-hover tbody tr:hover { background: #fcfcfd; }
    .sr-modern .search-input { width: 260px; }
    .sr-modern .header-actions { gap: .5rem; }
    .sr-modern .btn-rounded { border-radius: 999px; }
    .sr-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .sr-modern .btn-navy:hover { filter: brightness(1.03); }
    .sr-modern .btn-orange { background: ${ORANGE}; border-color: ${ORANGE}; color: #fff; }
    .sr-modern .btn-orange:hover { filter: brightness(1.03); }
    .sr-modern .action-btn { border-radius: 999px; padding: .35rem .6rem; }
    .sr-modern .pill-input, .sr-modern .pill-select { border-radius: 999px !important; padding: .45rem .9rem; }
    .sr-modern .badge-ellipsis { cursor: help; }
    /* Kurumsal pagination */
    .sr-modern .corporate-pagination .page-link { color: ${NAVY}; border-color: ${ORANGE}; }
    .sr-modern .corporate-pagination .page-item.active .page-link { background-color: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .sr-modern .corporate-pagination .page-link:hover { border-color: ${NAVY}; color: ${NAVY}; }
    .sr-modern .page-size-wrap { display:flex; align-items:center; gap:.5rem; white-space:nowrap; }
    .sr-modern .page-size-select { min-width:70px; padding-right:1.75rem; }
    .sr-modern .code-dark { color: #000 !important; }

    @media (max-width: 576px) {
      .sr-modern .search-input { width: 100%; }
      .sr-modern .table { font-size: 12px; }
      .sr-modern .table td, .sr-modern .table th { padding: .5rem; }
    }

    .sr-modern .header-actions .pill-input,
    .sr-modern .header-actions .pill-select,
    .sr-modern .header-actions .btn-navy {
      height: 40px;
      padding: .45rem .9rem;   /* CompaniesTable ile aynı */
      font-size: .95rem;
      line-height: 1.2;
      white-space: nowrap;
    }
    .sr-modern .header-actions .btn-navy i { line-height: 1; }
  `;

    const PAYMENT_STATUS_COLORS = {
        SUCCESS: "#71C29B", // Başarılı - Yeşil
        PENDING: "#E56A22", // Beklemede - Turuncu
        FAILED: "#E07A7A"   // Başarısız - Kırmızı
    };

    const ReportBadge = ({ text, type, style = {} }) => {
        let backgroundColor = "#B0B7C3"; // Varsayılan gri

        if (type === 'ACCOUNT_TYPE') {
            backgroundColor = ACCOUNT_TYPE_BADGE_COLORS[text] || backgroundColor;
        } else if (type === 'PAYMENT_STATUS') {
            backgroundColor = PAYMENT_STATUS_COLORS[text] || backgroundColor;
        }

        return (
            <span
                className="badge border-0"
                style={{
                    borderRadius: 999,
                    padding: ".35rem .5rem",
                    backgroundColor: backgroundColor,
                    color: "#fff",
                    ...style,
                }}
            >
            {type === 'ACCOUNT_TYPE' ? translateAccountType(text) : translatePaymentStatus(text)}
        </span>
        );
    };

    const renderAccountTypes = (types=[]) => {
        if (!types.length) return "-";
        if (types.length >= ALL_ACCOUNT_TYPES.length) {
            return (
                <ReportBadge
                    text="TÜM TİPLER" // Özel bir etiket için
                    type="ACCOUNT_TYPE"
                    style={{ backgroundColor: NAVY }} // Tüm tipler için Kurumsal Mavi
                />
            );
        }
        const shown = types.slice(0, 3).map((t, i) => (
            <ReportBadge key={i} text={t} type="ACCOUNT_TYPE" style={{ marginRight: '4px' }} />
        ));
        const rest = types.length > 3
            ? (
                <ReportBadge
                    key="ellipsis"
                    text="..."
                    type="ACCOUNT_TYPE"
                    style={{ backgroundColor: "#B0B7C3", cursor: 'help' }} // Gri elips
                    title={types.map(translateAccountType).join(", ")}
                />
            ) : null;
        return (<>{shown}{rest}</>);
    };

    // ---- API ----
    const fetchList = async (page=0, size=10, type="", email="", cId="", aId="", first=false) => {
        try {
            setErr(null);
            if (first) setLoading(true); else setRefreshing(true);

            const params = { page, size };
            if (type) params.scheduleType = type;
            if (email && email.trim()) params.emailLike = email.trim();
            if (cId && cId.trim()) params.companyId = cId.trim();
            if (aId && aId.trim()) params.accountId = aId.trim();

            const { data } = await axios.get(API.scheduled_reports_list, {
                params,
                headers: {},
            });

            if (data.statusCode === 200 && data.data) {
                const { content, totalElements, totalPages, number, size: ps } = data.data;
                setItems(content || []);
                setTotalElements(totalElements || 0);
                setTotalPages(totalPages || 0);
                setCurrentPage(number ?? 0);
                setPageSize(ps || size);
            } else {
                setErr("Zamanlanmış raporlar yüklenemedi");
            }
        } catch (e) {
            if (e.response?.status === 401 || e.response?.status === 403) {
                navigate("/login");
            } else {
                setErr(e.response?.data?.message || "Rapor listesi çekilirken hata");
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { fetchList(0, pageSize, "", "", "", "", true); }, []);

    // emailLike debounce
    useEffect(() => {
        if (isFirstSearch.current) { isFirstSearch.current = false; return; }
        const t = setTimeout(() => fetchList(0, pageSize, scheduleType, emailLike, companyId, accountId), 500);
        return () => clearTimeout(t);
    }, [emailLike]);

    // ---- Pagination helpers (CompaniesTable ile aynı mantık) ----
    const getPages = () => {
        const arr = [];
        const max = 5;
        let start = Math.max(0, currentPage - Math.floor(max / 2));
        let end = Math.min(totalPages - 1, start + max - 1);
        if (end - start + 1 < max) start = Math.max(0, end - max + 1);
        for (let i = start; i <= end; i++) arr.push(i);
        return arr;
    };

    const handlePageChange = (p) => {
        if (p >= 0 && p < totalPages) {
            setCurrentPage(p);
            fetchList(p, pageSize, scheduleType, emailLike, companyId, accountId);
        }
    };
    const handlePageSizeChange = (sz) => {
        setPageSize(sz);
        setCurrentPage(0);
        fetchList(0, sz, scheduleType, emailLike, companyId, accountId);
    };

    /**
     * Son çalışmanın sonucu. Önceden yalnız tarih vardı ve üç durum aynı görünüyordu:
     * mail gitti / kayıt yoktu / hata aldı. Tarih tek başına yanıltıyordu.
     */
    const renderRunStatus = (r) => {
        if (!r.lastRunStatus) return null;
        const map = {
            SUCCESS: { color: "#198754", label: "Gönderildi" },
            NO_DATA: { color: "#6c757d", label: "Kayıt yok" },
            FAILED:  { color: "#dc3545", label: "Hata" },
        };
        const m = map[r.lastRunStatus];
        if (!m) return null;
        return (
            <div
                style={{ color: m.color, fontSize: ".72rem", fontWeight: 600 }}
                title={r.lastRunMessage || m.label}
            >
                {m.label}
            </div>
        );
    };

    // ---- CRUD helpers ----
    const remove = async (id) => {
        try {
            await axios.delete(API.scheduled_reports_detail(id), {
                headers: {},
            });
            fetchList(currentPage, pageSize, scheduleType, emailLike, companyId, accountId);
        } catch (e) {
            alert(e.response?.data?.message || "Rapor silinemedi");
        }
    };

    // ---- Modals ----
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteItem, setDeleteItem] = useState(null);

    const openCreate = () => setCreateOpen(true);
    const closeCreate = (refresh=false) => { setCreateOpen(false); if (refresh) fetchList(currentPage, pageSize, scheduleType, emailLike, companyId, accountId); };
    const openEdit = (it) => { setEditItem(it); setEditOpen(true); };
    const closeEdit = (refresh=false) => { setEditOpen(false); setEditItem(null); if (refresh) fetchList(currentPage, pageSize, scheduleType, emailLike, companyId, accountId); };
    const openDelete = (it) => { setDeleteItem(it); setDeleteOpen(true); };
    const closeDelete = (refresh=false) => { setDeleteOpen(false); setDeleteItem(null); if (refresh) fetchList(currentPage, pageSize, scheduleType, emailLike, companyId, accountId); };

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewItem, setPreviewItem] = useState(null);
    const openPreview = (it) => { setPreviewItem(it); setPreviewOpen(true); };
    const closePreview = () => { setPreviewOpen(false); setPreviewItem(null); };

    /**
     * Excel indirme. Yanıt ikili (binary) olduğu için blob istenir; hata
     * durumunda gövde JSON döndüğünden blob'u metne çevirip mesajı okuruz.
     */
    const exportReport = async (it) => {
        if (!it?.id) return;
        try {
            const res = await axios.get(API.scheduled_report_export(it.id), {
                responseType: "blob",
                withCredentials: true,
            });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = `rapor_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            let msg = "Rapor indirilemedi";
            try {
                const text = await e?.response?.data?.text?.();
                if (text) msg = JSON.parse(text)?.message || msg;
            } catch { /* gövde okunamadıysa genel mesaj kalır */ }
            alert(msg);
        }
    };
    

    return (
        <div className="page-content sr-modern">
            <style>{styles}</style>
            <div className="container-fluid">
                <Row>
                    <Col>
                        <TableCard>
                            <CardHeader className="bg-white border-0 rounded-top-4">
                                {/* DESKTOP header */}
                                <div className="d-none d-md-block">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <div>
                                            <h5 className="mb-1 fw-bold text-dark">Zamanlanmış Raporlar</h5>
                                            <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                                                {totalElements > 0 ? `Toplam ${totalElements} rapor` : "Rapor bulunamadı"}
                                            </p>
                                        </div>
                                        <Button className="d-flex align-items-center gap-1 btn-rounded btn-navy" onClick={openCreate}>
                                            <i className="bx bx-plus" /> Zamanlanmış Rapor Oluştur
                                        </Button>
                                    </div>
                                    <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
                                        <Input
                                            type="select"
                                            value={scheduleType}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setScheduleType(v);
                                                setCurrentPage(0);
                                                fetchList(0, pageSize, v, emailLike, companyId, accountId);
                                            }}
                                            className="pill-select"
                                            style={{ width: 200 }}
                                        >
                                            <option value="">Tüm Zamanlama</option>
                                            <option value="DAILY">Günlük</option>
                                            <option value="WEEKLY">Haftalık</option>
                                            <option value="MONTHLY">Aylık</option>
                                            <option value="QUARTERLY">3 Aylık</option>
                                        </Input>
                                        <Input
                                            innerRef={emailRef}
                                            type="search"
                                            placeholder={animatedPlaceholder}
                                            value={emailLike}
                                            onChange={(e) => setEmailLike(e.target.value)}
                                            className="search-input pill-input"
                                            inputMode="email"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            spellCheck={false}
                                        />
                                    </div>
                                </div>

                                {/* MOBILE header */}
                                <div className="d-md-none">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <h5 className="mb-0 fw-semibold">Zamanlanmış Raporlar</h5>
                                        <Button
                                            size="sm"
                                            className="d-flex align-items-center gap-1 btn-rounded btn-navy"
                                            onClick={openCreate}
                                        >
                                            <i className="bx bx-plus" /> Ekle
                                        </Button>
                                    </div>
                                    <p className="mb-2 text-muted" style={{ fontSize: ".9rem" }}>
                                        {totalElements > 0 ? `Toplam ${totalElements} rapor` : "Rapor bulunamadı"}
                                    </p>
                                    <div className="d-grid gap-2">
                                        <Input
                                            innerRef={emailRef}
                                            type="search"
                                            placeholder={animatedPlaceholder}
                                            value={emailLike}
                                            onChange={(e) => setEmailLike(e.target.value)}
                                            className="pill-input"
                                            inputMode="email"
                                            autoComplete="off"
                                            autoCorrect="off"
                                            spellCheck={false}
                                        />
                                        <Input
                                            type="select"
                                            value={scheduleType}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setScheduleType(v);
                                                setCurrentPage(0);
                                                fetchList(0, pageSize, v, emailLike, companyId, accountId);
                                            }}
                                            className="pill-select"
                                        >
                                            <option value="">Tüm Zamanlama</option>
                                            <option value="DAILY">Günlük</option>
                                            <option value="WEEKLY">Haftalık</option>
                                            <option value="MONTHLY">Aylık</option>
                                            <option value="QUARTERLY">3 Aylık</option>
                                        </Input>
                                    </div>
                                </div>
                            </CardHeader>

                            <CardBody className="pt-2">
                                {err && (
                                    <Alert color="danger" className="mb-3 rounded-4">
                                        <i className="bx bx-error-circle me-2" />
                                        {err}
                                        <button
                                            className="btn btn-sm btn-outline-danger ms-2 btn-rounded"
                                            onClick={() => fetchList(currentPage, pageSize, scheduleType, emailLike, companyId, accountId)}
                                        >
                                            Tekrar Dene
                                        </button>
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center p-5">
                                        <Spinner size="lg" color="primary" />
                                        <h6 className="mt-3 text-muted">Raporlar yükleniyor...</h6>
                                    </div>
                                ) : (
                                    <>
                                        <div className={`d-flex justify-content-between align-items-center mb-2 ${refreshing ? 'opacity-50' : ''}`}>
                                            <div className="text-muted" style={{ fontSize: ".9rem" }}>
                                                {totalElements > 0 && (
                                                    <>Toplam <strong>{totalElements}</strong> kayıt • Sayfa <strong>{currentPage + 1}</strong> / {totalPages}</>
                                                )}
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <button
                                                    className="btn btn-light btn-sm btn-rounded"
                                                    onClick={() => fetchList(currentPage, pageSize, scheduleType, emailLike, companyId, accountId)}
                                                    title="Yenile"
                                                >
                                                    <i className={`bx bx-refresh ${refreshing ? 'bx-spin' : ''}`} />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="table-responsive rounded-4 border">
                                            <Table className="table table-hover mb-0">
                                                <Thead>
                                                    <Tr>
                                                        <Th>Alıcı E-posta</Th>
                                                        <Th>Zamanlama</Th>
                                                        <Th className="d-none d-sm-table-cell">Gün</Th>
                                                        <Th className="d-none d-sm-table-cell">Min Tutar</Th>
                                                        <Th className="d-none d-md-table-cell">Cari/Bayi</Th>
                                                        <Th className="d-none d-xl-table-cell">Cari/Bayi Türleri</Th>
                                                        <Th className="d-none d-xl-table-cell">Durum Filtreleri</Th>
                                                        <Th className="d-none d-lg-table-cell">Son Çalışma</Th>
                                                        <Th className="d-none d-xl-table-cell">Sonraki Çalışma</Th>
                                                        <Th className="text-end">İşlemler</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {items.length ? items.map((r) => (
                                                        <Tr key={r.id} className="border-bottom">
                                                            <Td>
                                                                <TruncatedText
                                                                    text={r.recipientEmail || "-"}
                                                                    maxLength={28}
                                                                    className="fw-semibold text-dark"
                                                                />
                                                            </Td>                                                            <Td>{translateScheduleType(r.scheduleType)}</Td>
                                                            <Td className="d-none d-sm-table-cell">{r.scheduleDay ?? "-"}</Td>
                                                            <Td className="d-none d-sm-table-cell">{r.minAmount != null ? `₺${Number(r.minAmount).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-"}</Td>
                                                            <Td className="d-none d-md-table-cell">
                                                                <TruncatedText
                                                                    text={r.accountName || "-"}
                                                                    maxLength={20}
                                                                />
                                                            </Td>
                                                            <Td className="d-none d-xl-table-cell">{renderAccountTypes(r.accountTypes)}</Td>
                                                            <Td className="d-none d-xl-table-cell">
                                                                {(r.statuses && r.statuses.length)
                                                                    ? r.statuses.map((s, i) => (
                                                                        <ReportBadge key={i} text={s} type="PAYMENT_STATUS" style={{ marginRight: '4px' }} />
                                                                    ))
                                                                    : "-"}
                                                            </Td>
                                                            <Td className="d-none d-lg-table-cell">
                                                                {formatDate(r.lastRunDate)}
                                                                {renderRunStatus(r)}
                                                            </Td>
                                                            <Td className="d-none d-xl-table-cell">{formatDate(r.nextRunDate)}</Td>
                                                            <Td className="text-end">
                                                                <div className="d-flex gap-1 justify-content-end flex-wrap">
                                                                    <button
                                                                        className="btn btn-sm action-btn btn-navy"
                                                                        title="Görüntüle"
                                                                        onClick={() => openPreview(r)}
                                                                    >
                                                                        <i className="bx bx-show" />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm action-btn btn-navy"
                                                                        title="Excel indir"
                                                                        onClick={() => exportReport(r)}
                                                                    >
                                                                        <i className="bx bx-download" />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm action-btn btn-navy"
                                                                        title="Düzenle"
                                                                        onClick={() => openEdit(r)}
                                                                    >
                                                                        <i className="bx bx-edit" />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm action-btn"
                                                                        style={{ background: "#dc3545", borderColor: "#dc3545", color: "#fff" }}
                                                                        title="Sil"
                                                                        onClick={() => openDelete(r)}
                                                                    >
                                                                        <i className="bx bx-trash" />
                                                                    </button>
                                                                </div>
                                                            </Td>
                                                        </Tr>
                                                    )) : (
                                                        <Tr>
                                                            <Td colSpan="10" className="text-center py-4 text-muted">
                                                                <i className="bx bx-time-five d-block mb-2 display-6" />
                                                                Zamanlanmış rapor bulunamadı
                                                                {emailLike && (
                                                                    <div className="mt-2">
                                                                        <small>Arama teriminizi değiştirmeyi deneyin</small>
                                                                    </div>
                                                                )}
                                                            </Td>
                                                        </Tr>
                                                    )}
                                                </Tbody>
                                            </Table>
                                        </div>

                                        {/* Pagination – CompaniesTable görünümü */}
                                        {totalElements > 0 && (
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <div className="text-muted" style={{ fontSize: ".9rem" }}>
                                                    {totalElements} kayıttan {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)} arası gösteriliyor
                                                </div>

                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="d-flex align-items-center gap-2 page-size-wrap">
                                                        <span className="text-muted" style={{ fontSize: ".9rem" }}>Sayfa başına</span>
                                                        <select
                                                            className="form-select form-select-sm rounded-pill page-size-select"
                                                            value={pageSize}
                                                            onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                                                        >
                                                            {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                                                        </select>
                                                    </div>

                                                    <nav>
                                                        <ul className="pagination pagination-sm mb-0 corporate-pagination">
                                                            <li className={`page-item ${currentPage === 0 ? "disabled" : ""}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                                    disabled={currentPage === 0}
                                                                >
                                                                    <i className="bx bx-chevron-left" />
                                                                </button>
                                                            </li>
                                                            {getPages().map(p => (
                                                                <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                                                                    <button className="page-link" onClick={() => handlePageChange(p)}>
                                                                        {p + 1}
                                                                    </button>
                                                                </li>
                                                            ))}
                                                            <li className={`page-item ${currentPage >= totalPages - 1 ? "disabled" : ""}`}>
                                                                <button
                                                                    className="page-link"
                                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                                    disabled={currentPage >= totalPages - 1}
                                                                >
                                                                    <i className="bx bx-chevron-right" />
                                                                </button>
                                                            </li>
                                                        </ul>
                                                    </nav>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardBody>

                            {/* Modals */}
                            <ScheduledReportCreateModal
                                isOpen={createOpen}
                                toggle={() => closeCreate(false)}
                                onSaved={() => closeCreate(true)}
                            />
                            <ScheduledReportEditModal
                                isOpen={editOpen}
                                toggle={() => closeEdit(false)}
                                onSaved={() => closeEdit(true)}
                                report={editItem}
                            />
                            <ScheduledReportDeleteModal
                                isOpen={deleteOpen}
                                toggle={() => closeDelete(false)}
                                onConfirm={remove}
                                report={deleteItem}
                            />
                            <ScheduledReportPreviewModal
                                isOpen={previewOpen}
                                toggle={closePreview}
                                report={previewItem}
                                onExport={exportReport}
                            />
                        </TableCard>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default ScheduledReportsTable;